/**
 * `primary` — THE PRIMARY INTERACTIVE CHART (R765, owner mandate 17:2xZ). Price candles +
 * session CVD on ONE shared time scale, zoom/pan/scroll live.
 *
 * ⛔ THIS PANEL IS WHY R504's SCOPE STAMP IS GONE. "Heads-up only — the cockpit column is
 * untouched" was a SAFETY property of one slice, and it silently became a PERMANENT STATE:
 * every part needed here already existed and shipped, and the promotion step was never
 * dispatched. R765① names that as the day's class — built, keyed, landed, never CONNECTED.
 * The owner lifted the bar by word, and this id is the connection.
 *
 * ⛔⛔ KEEP PROSE OUT OF THE UNION BELOW, AND OUT OF THIS BLOCK IF IT WOULD ECHO THE MATCHER.
 * `scripts/check-mounts.mjs:91` finds this union by scanning for its declaration and capturing
 * NON-GREEDILY UP TO THE FIRST SEMICOLON. Two ways to break it, and I hit BOTH in one edit
 * (2026-08-25, at my own hand):
 *   1. a `;` inside the union — even in a comment — truncates the capture to prose, so the
 *      gate parses ZERO ids;
 *   2. ***quoting the matcher's own search text in a comment ABOVE the union makes the scan
 *      match THE COMMENT FIRST*** — the explanation of the trap re-created the trap, one
 *      layer earlier, and the symptom was identical.
 * Both times it reported *"parsed to ZERO ids — this run graded NOTHING"* and REFUSED.
 * ⭐ It failed loud rather than certifying an empty sweep: the harness-proof did its job twice.
 */
/**
 * R771① — THE PAIR. Two interactive charts side by side, each on its own rung. `primary` is kept
 * as a legal id so nothing that still names it breaks mid-migration; `primaryA`/`primaryB` are
 * what App.svelte lays out today.
 *
 * ⛔ THIS COMMENT LIVES ABOVE THE UNION, NOT INSIDE IT, AND THAT IS LOAD-BEARING. I first put it
 * between `=` and the first `|`, and `check:mounts` parsed the union to ZERO ids and REFUSED
 * (rc=2, "this run graded NOTHING"). *** A DOCBLOCK INSIDE A PARSED DECLARATION CAN DISARM THE
 * GATE THAT READS IT *** — and the only reason it cost minutes rather than a shipped hole is
 * that the gate has a vacuity guard and would not pass on an empty parse.
 */
export type PanelId =
  | "primary"
  | "primaryA"
  | "primaryB"
  | "range"
  | "paneA" | "paneB" | "ladder" | "tick" | "tide" | "news"
  /** SPX net-premium tide (R677①). Lower row, with the other page-wide panels. */
  | "netprem"
  | "tideIndex"
  | "tideEtf";

/** ORDER 28-C: only chart and tide surfaces own maximize chrome. */
export type MaximizablePanelId =
  | "primaryA"
  | "primaryB"
  | "range"
  | "tideIndex"
  | "tideEtf"
  | "tide";

export const MAXIMIZABLE_PANEL_IDS: MaximizablePanelId[] = [
  "primaryA",
  "primaryB",
  "range",
  "tideIndex",
  "tideEtf",
  "tide",
];

const MAXIMIZABLE_PANEL_SET = new Set<PanelId>(MAXIMIZABLE_PANEL_IDS);

export function isMaximizablePanel(id: PanelId): id is MaximizablePanelId {
  return MAXIMIZABLE_PANEL_SET.has(id);
}

export interface Cell {
  col: number;
  row: number;
  span: number;
  /**
   * How many ROWS this cell occupies. 1 for everything except the exposure rail.
   *
   * ⛔ ADDED BY R765 TO CURE A REGRESSION I SHIPPED INTO THE LOCAL BUILD AND CAUGHT BY LOOKING.
   * Promoting the primary chart to row 1 left the rail alone beside it, and the rail's content
   * (GEX ladder + dealer positioning + flow) is ~1000px against the chart's 460px. A CSS grid
   * row is as tall as its tallest cell, so row 1 stretched to the RAIL and the chart sat in a
   * ~900px void with the rung panes pushed below it.
   * ⇒ ***MOVING A TALL SIBLING'S NEIGHBOUR OUT OF ITS ROW DOES NOT MAKE THE ROW SHORTER; IT
   * MAKES THE HOLE VISIBLE.*** The rail spans both chart rows, which is what it always did in
   * effect when the two rung panes filled row 1 beside it.
   */
  rowSpan: number;
}

const PANEL_IDS = new Set<PanelId>([
  "primary",
  "primaryA",
  "primaryB",
  "range",
  "paneA",
  "paneB",
  "ladder",
  "tick",
  "tide",
  "news",
  "netprem",
  "tideIndex",
  "tideEtf",
]);

/**
 * ⚠ `flow` AND `vsplit` ARE RETIRED — R812①, the owner's layout law. They became per-chart
 * (`flowA`/`flowB`) with EXPLICIT branches below, like `primaryA`/`paneA`.
 * ⛔ AND THEY ARE GONE FROM `PanelId` ENTIRELY, not merely from this list. Keeping them declared
 * would oblige `check:mounts` to hold a marker for a panel nothing mounts, and `assignCells` would
 * refuse them anyway. A stored layout naming an old id now falls back and NAMES the loss, which is
 * `parseStoredLayout`'s designed behaviour — the loud outcome, not a silent empty column.
 */
const LOWER_PANEL_ORDER: PanelId[] = ["tick", "tide", "news", "netprem"];

/** Explicit child -> cell. Nothing is ever placed by falling through to the next free slot. */
export function assignCells(children: PanelId[], columns: number): Record<PanelId, Cell> {
  if (!Number.isInteger(columns) || columns < 3) {
    throw new RangeError(`layout: columns must be an integer >= 3; received ${columns}`);
  }

  const cells = {} as Record<PanelId, Cell>;
  const hasRungPaneRow = children.includes("paneA") || children.includes("paneB");
  const chartStackLastRow = hasRungPaneRow ? 2 : 1;
  const expiryRow = chartStackLastRow + 1;
  const rangeRow = children.includes("tideIndex") || children.includes("tideEtf") ? expiryRow + 1 : expiryRow;
  const lowerStartRow = rangeRow + 1;
  for (const child of children) {
    if (!PANEL_IDS.has(child)) throw new TypeError(`layout: unknown panel ${JSON.stringify(child)}`);

    // ROW 1 — the primary interactive chart, with the exposure rail as its sibling column.
    // It takes every column the ladder does not, so the widest surface belongs to the chart
    // the owner actually drives (R765③).
    if (child === "primary") cells[child] = { col: 1, row: 1, span: columns - 1, rowSpan: 1 };
    // R771① — THE PAIR. Two interactive charts side by side in row 1, one column each, with the
    // rail keeping column `columns`. Each carries its own rung, so the owner reads 1m against 12m.
    else if (child === "primaryA") cells[child] = { col: 1, row: 1, span: 1, rowSpan: 1 };
    else if (child === "primaryB") cells[child] = { col: 2, row: 1, span: 1, rowSpan: 1 };
    // The rail spans every chart row — see Cell.rowSpan for the void this cures.
    // is as tall as its tallest cell: the rail is ~1700px against the chart's 460px, so leaving the
    // rail in row 1 alone stretched that row and opened a ~900px void beside the chart.
    // ⇒ ***MOVING A TALL SIBLING'S NEIGHBOUR OUT OF ITS ROW DOES NOT MAKE THE ROW SHORTER; IT MAKES
    //   THE HOLE VISIBLE.*** The rail spans the rows that are actually mounted: chart + expiry
    //   tide today, or chart + extracted CVD pane + expiry tide when that separate work lands.
    //   Preserving an absent pane's row would spend a second grid gap on empty history.
    else if (child === "ladder") cells[child] = { col: columns, row: 1, span: 1, rowSpan: expiryRow };
    // ROW 2 — the rung panes. ⛔ MOVED, NEVER DELETED (R765③ / R519: wire the mount first).
    // A pane that vanishes is indistinguishable from a pane that broke, and this repo has
    // already paid for that twice — see parseStoredLayout's docblock.
    else if (child === "paneA") cells[child] = { col: 1, row: 2, span: 1, rowSpan: 1 };
    else if (child === "paneB") cells[child] = { col: 2, row: 2, span: 1, rowSpan: 1 };
    // ORDER 3b expiry-tide panels follow the last mounted chart row. The standalone pane pair is
    // currently retired, so this closes row 2; if either pane is present, expiry stays on row 3.
    else if (child === "tideIndex") cells[child] = { col: 1, row: expiryRow, span: 1, rowSpan: 1 };
    else if (child === "tideEtf") cells[child] = { col: 2, row: expiryRow, span: 1, rowSpan: 1 };
    // ORDER 28: CHART C is the compact bridge between the primary pair and their tide row. It
    // spans exactly A/B's two columns; the exposure rail remains pinned in its own third column.
    else if (child === "range") cells[child] = { col: 1, row: rangeRow, span: columns - 1, rowSpan: 1 };
    else {
      const index = LOWER_PANEL_ORDER.indexOf(child);
      /**
       * ⛔ THIS FUNCTION'S OWN DOCBLOCK PROMISES *"nothing is ever placed by falling through"*,
       * AND UNTIL NOW IT DID. An id that is legal in `PANEL_IDS` but absent from
       * `LOWER_PANEL_ORDER` reached here with `index === -1` and was placed at
       * ** col 0, row 2 ** — not a slot, and CSS grid columns are 1-based, so it silently
       * collided with whatever else landed there. I hit it the moment I added `primaryA`
       * before giving it a branch: BOTH charts came back col 0, row 2, overlapping.
       * ⇒ *** A LOOKUP THAT RETURNS -1 IS AN ANSWER ABOUT THE TABLE, NOT ABOUT THE PANEL ***,
       * and arithmetic on it produces a plausible-looking cell instead of a refusal (law 1).
       */
      if (index === -1) {
        throw new TypeError(
          `layout: ${JSON.stringify(child)} is a declared panel with no placement — add an explicit ` +
            `branch or list it in LOWER_PANEL_ORDER. It must never fall through to col 0.`,
        );
      }
      cells[child] = {
        col: (index % columns) + 1,
        // Start immediately below the mounted chart stack. `expiryRow` is dynamic because the
        // separately extracted CVD pane may be present or absent; neither state may leave a hole.
        row: Math.floor(index / columns) + lowerStartRow,
        span: 1,
        rowSpan: 1,
      };
    }
  }
  return cells;
}

export interface PriceWindow {
  loP: number;
  hiP: number;
}

/** ORDER 8: one editable type scale for every price label on the two dealer ladders. */
export const LABEL_FONT_PRIMARY = 16;
export const LABEL_FONT_SECONDARY = 11;
export const LABEL_FONT_MICRO = 10;
export const LABEL_MIN_GAP_PX = 20;

/**
 * Admit a browser measurement before responsive SVG geometry consumes it.
 *
 * A hidden element legitimately reports zero through Svelte's clientWidth binding. That is an
 * UNMEASURED rendering state, not a dimension to pass into the strict scaler below. The optional
 * cap preserves panels whose explicit height prevents them from scaling above their viewBox width.
 */
export function measuredSvgWidth(
  renderedWidth: number,
  viewBoxWidth: number,
  capAtViewBox = false,
): number | null {
  if (!Number.isFinite(renderedWidth)) throw new TypeError("SVG measurement: rendered width must be finite");
  if (renderedWidth === 0) return null;
  if (renderedWidth < 0) throw new RangeError("SVG measurement: rendered width must not be negative");
  if (!Number.isFinite(viewBoxWidth)) throw new TypeError("SVG measurement: viewBox width must be finite");
  if (viewBoxWidth <= 0) throw new RangeError("SVG measurement: viewBox width must be positive");
  return capAtViewBox ? Math.min(renderedWidth, viewBoxWidth) : renderedWidth;
}

/** Convert rendered CSS pixels into viewBox units; a literal zero measurement returns the wait sentinel. */
export function renderedPxToSvgUnits(renderedPx: 0, viewBoxWidth: number, renderedWidth: number): null;
export function renderedPxToSvgUnits(renderedPx: number, viewBoxWidth: number, renderedWidth: 0): null;
export function renderedPxToSvgUnits(renderedPx: number, viewBoxWidth: number, renderedWidth: number): number;
export function renderedPxToSvgUnits(
  renderedPx: number,
  viewBoxWidth: number,
  renderedWidth: number,
): number | null {
  if (!Number.isFinite(viewBoxWidth)) throw new TypeError("SVG scale: viewBox width must be finite");
  if (![renderedPx, renderedWidth].every(Number.isFinite)) {
    throw new TypeError("SVG scale: measured dimensions must be finite");
  }
  if (viewBoxWidth <= 0) {
    throw new RangeError(`SVG scale: viewBox width must be positive; received ${viewBoxWidth}`);
  }
  if (renderedPx === 0 || renderedWidth === 0) return null;
  if (renderedPx < 0 || renderedWidth < 0) {
    throw new RangeError(`SVG scale: measured dimensions must not be negative; received ${renderedPx}, ${renderedWidth}`);
  }
  return renderedPx * viewBoxWidth / renderedWidth;
}

export interface DealerLabelCandidate {
  id: string;
  preferredY: number;
  /** Lower values win the upper lane when clamped labels share an edge. */
  priority?: number;
  /** A53: the height a second text line occupies below this baseline (0 or absent: one line). */
  extraBelow?: number;
}

/**
 * Resolve SVG text baselines into non-overlapping vertical lanes.
 *
 * The dealer rail previously drew LIVE and GAMMA FLIP from independent y expressions. When their
 * prices converged, both expressions returned the same baseline and the labels became one garbled
 * string. This resolver consumes every primary wall baseline together, preserves price order, and
 * enforces the ruled 20px separation for 16px text. It is price-agnostic, so exact-wall and future
 * spot values take the same path.
 */
export function resolveDealerLabelBaselines(
  labels: DealerLabelCandidate[],
  bounds: { minY: number; maxY: number },
  minGapPx = LABEL_MIN_GAP_PX,
): ReadonlyMap<string, number> {
  if (!Number.isFinite(bounds.minY) || !Number.isFinite(bounds.maxY) || bounds.maxY < bounds.minY) {
    throw new RangeError(`label layout: invalid bounds ${bounds.minY}..${bounds.maxY}`);
  }
  if (!Number.isFinite(minGapPx) || minGapPx <= 0) {
    throw new RangeError(`label layout: min gap must be positive; received ${minGapPx}`);
  }
  if (new Set(labels.map((label) => label.id)).size !== labels.length) {
    throw new TypeError("label layout: every rendered label needs a unique id");
  }
  if (labels.some((label) => !Number.isFinite(label.preferredY))) {
    throw new TypeError("label layout: every preferred baseline must be finite");
  }
  if (labels.some((label) => label.priority !== undefined && !Number.isFinite(label.priority))) {
    throw new TypeError("label layout: every label priority must be finite");
  }
  if (labels.some((label) => label.extraBelow !== undefined && !(Number.isFinite(label.extraBelow) && label.extraBelow >= 0))) {
    throw new TypeError("label layout: every extraBelow must be a finite height >= 0");
  }
  const below = (label: DealerLabelCandidate) => label.extraBelow ?? 0;

  const placed = labels
    .map((label) => ({ ...label, y: Math.min(bounds.maxY, Math.max(bounds.minY, label.preferredY)) }))
    .sort((a, b) =>
      a.y - b.y
      || (a.priority ?? Number.MAX_SAFE_INTEGER) - (b.priority ?? Number.MAX_SAFE_INTEGER)
      || a.id.localeCompare(b.id));

  for (let i = 1; i < placed.length; i++) {
    placed[i].y = Math.max(placed[i].y, placed[i - 1].y + minGapPx + below(placed[i - 1]));
  }
  if (placed.length && placed.at(-1)!.y + below(placed.at(-1)!) > bounds.maxY) {
    placed[placed.length - 1].y = bounds.maxY - below(placed.at(-1)!);
    for (let i = placed.length - 2; i >= 0; i--) {
      placed[i].y = Math.min(placed[i].y, placed[i + 1].y - minGapPx - below(placed[i]));
    }
  }
  if (placed.length && placed[0].y < bounds.minY) {
    const shift = bounds.minY - placed[0].y;
    for (const label of placed) label.y += shift;
  }

  return new Map(placed.map((label) => [label.id, label.y]));
}

export type LadderPlacement =
  | { kind: "at"; y: number }
  | { kind: "clamped"; y: number; edge: "top" | "bottom" };

export function ladderRowPlacement(
  price: number,
  win: PriceWindow,
  heightPx: number,
): LadderPlacement {
  if (win.hiP <= win.loP) return { kind: "clamped", y: 0, edge: "top" };
  if (price > win.hiP) return { kind: "clamped", y: 0, edge: "top" };
  if (price < win.loP) return { kind: "clamped", y: heightPx, edge: "bottom" };
  return {
    kind: "at",
    y: heightPx - ((price - win.loP) * heightPx) / (win.hiP - win.loP),
  };
}

/**
 * SPOT ON A DRAWN LADDER — the placement §GEX-2 never had (R805①).
 *
 * ⛔ WHY THIS EXISTS RATHER THAN A SECOND CALL TO `ladderRowPlacement` IN THE COMPONENT.
 * That helper takes a PRICE WINDOW; it cannot know which end of the window is drawn at the top.
 * `Gex2Panel` and `DealerRailPanel` both render `[...shown].reverse()` with `y(0)` at the TOP, so
 * the window's `hiP` is the FIRST row — but nothing in the type system says so. `DealerRailPanel`
 * already carries the scar (its §140 comment): reversing the rows alone once left SPOT and GAMMA
 * FLIP drawing at their mirrored positions on a ladder that LOOKED correct.
 * ⇒ ***A MIRRORED REFERENCE LINE IS WORSE THAN A MISSING ONE: it reads as a measurement.***
 *
 * So this function derives the orientation FROM THE DRAWN ARRAY and REFUSES when it cannot verify
 * it. `absent` is a first-class result carrying its own reason — never a silent `null` the surface
 * can render as a blank, and never a guessed orientation.
 *
 * ⚠ THE REFUSAL IS THE POINT. If a future change makes the rows ascend, this returns `absent` with
 * a reason the panel prints — it does NOT quietly draw the line upside down.
 */
export type LadderSpot =
  | { kind: "at"; y: number }
  | { kind: "clamped"; y: number; edge: "top" | "bottom" }
  | { kind: "absent"; reason: string };

export function ladderSpotPlacement(
  spot: number | null | undefined,
  /** Strikes IN DRAW ORDER — index 0 is the row rendered at the TOP. */
  displayStrikes: readonly number[],
  geom: { padT: number; rowH: number },
): LadderSpot {
  // ⛔ ZERO IS A PRICE, NOT AN EMPTY VALUE — the same refusal `liveMarker` makes, for the same
  // reason: drawing it would be absurd, and treating it as absence without saying so is silent
  // degradation.
  if (spot === null || spot === undefined || !Number.isFinite(spot) || spot <= 0) {
    return { kind: "absent", reason: "no usable spot to place" };
  }
  if (displayStrikes.length < 2) {
    return { kind: "absent", reason: "the ladder has fewer than two strikes to place a spot against" };
  }

  const top = displayStrikes[0];
  const bottom = displayStrikes[displayStrikes.length - 1];
  if (!Number.isFinite(top) || !Number.isFinite(bottom)) {
    return { kind: "absent", reason: "the ladder's end strikes are not both numbers" };
  }
  if (top === bottom) {
    return { kind: "absent", reason: "every drawn strike is the same price" };
  }
  // THE ORIENTATION GUARD. Descending (highest first) is what both ladders draw today.
  if (top < bottom) {
    return {
      kind: "absent",
      reason: "the ladder rows ascend; refusing to place spot on an orientation this helper cannot verify",
    };
  }

  // Row CENTRES, not row tops: the line must sit where the strike label sits.
  const plotTop = geom.padT + geom.rowH / 2;
  const plotHeight = (displayStrikes.length - 1) * geom.rowH;
  const placed = ladderRowPlacement(spot, { loP: bottom, hiP: top }, plotHeight);
  return placed.kind === "at"
    ? { kind: "at", y: plotTop + placed.y }
    : { kind: "clamped", y: plotTop + placed.y, edge: placed.edge };
}


/* ==============================================================================================
 * PHASE 2 — PERSISTED ARRANGEMENT. Spec §3 + GK-ff's B4 (`e55c5e17`).
 *
 * ⛔ THE PURE HALF LIVES HERE ON PURPOSE. B2/B3 cost the panes twice: a decision inside a `.svelte`
 * file is a decision no arm can reach. Parsing, validating and FALLING BACK are decisions, so they
 * are functions. Only the drag gestures and `localStorage` calls belong in the component.
 * ⚠ B4: *arm the SAYING, not only the falling back* — a silent fallback is indistinguishable from
 * a correct load, so `reason` is part of the return value and not a console line.
 * ============================================================================================== */

export interface StoredLayout {
  /** The arrangement to render — ALWAYS usable, even when the stored value was not. */
  order: PanelId[];
  /** TRUE when the stored value could not be used as-is. */
  fellBack: boolean;
  /** ⛔ B4: the surface renders this. Null ONLY when the stored layout was used verbatim. */
  reason: string | null;
}

/**
 * Read an arrangement that a previous version of this app wrote. Every failure mode returns a
 * USABLE order plus the reason, because ***AN UNREACHABLE PANEL IS INDISTINGUISHABLE FROM A
 * DELETED ONE*** — the trap this repo has now paid for in a chart pane and a ladder.
 */
export function parseStoredLayout(raw: string | null, known: PanelId[]): StoredLayout {
  const fallback = (reason: string): StoredLayout => ({ order: [...known], fellBack: true, reason });

  if (raw === null || raw.trim() === "") return fallback("no saved layout — using the default");

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return fallback("saved layout was unreadable — using the default");
  }
  if (!Array.isArray(parsed) || parsed.some((x) => typeof x !== "string")) {
    return fallback("saved layout was not a list of panels — using the default");
  }

  const stored = parsed as string[];
  const knownSet = new Set<string>(known);
  const unknown = stored.filter((id) => !knownSet.has(id));
  if (unknown.length > 0) {
    // A panel this build does not have. Falling back SILENTLY would hide the loss.
    return fallback(`saved layout names ${unknown.length} panel(s) this version does not have — using the default`);
  }
  const missing = known.filter((id) => !stored.includes(id));
  if (missing.length > 0) {
    // ⚠ NOT a fallback: a NEW panel must appear rather than vanish because an old layout predates it.
    return {
      order: [...(stored as PanelId[]), ...missing],
      fellBack: true,
      reason: `${missing.length} new panel(s) appended to your saved layout`,
    };
  }
  return { order: stored as PanelId[], fellBack: false, reason: null };
}

/* ------------------------------------------------------------------------------------
 * THE DIAGNOSTIC-STRIP PREFERENCE (owner word 16:33Z: the strip is noise on his surface).
 *
 * Parsing lives HERE, beside the layout parser, for the reason this module already states:
 * only the storage CALL belongs in the component. A `.svelte` ternary is unreachable by the
 * suite (M10) and that is exactly where the `CVD (session)` mislabel hid for a lane's life.
 * ---------------------------------------------------------------------------------- */

export const DIAG_PREF_KEY = "spx.hud.diag";

/**
 * Read the stored preference. **DEFAULT OFF — the owner's surface is the default surface.**
 *
 * ⚠ Anything unrecognised reads as OFF rather than throwing: a corrupt pref must not be able
 * to blank the chart, and the cost of guessing wrong here is one hidden instrument strip that
 * a seat re-enables with one click. That asymmetry is why this one coerces where `cvdAskLimit`
 * refuses — a bad span there renders a wrong NUMBER, a bad pref here hides a panel.
 */
export function parseDiagPref(raw: string | null): boolean {
  return raw === "1";
}

/** The value to persist. Kept next to the parser so the two cannot drift apart. */
export function serializeDiagPref(on: boolean): string {
  return on ? "1" : "0";
}
